import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { areEqual } from 'Utils/equalityChecks';
import { firstCompanyIdSelector } from 'Containers/Projects/selectors';
import { setToaster } from 'Containers/Core/actions';
import { SpinnerBlock } from 'Components/SpinnerBlock';
import { PurpleButton, LoadMoreButton } from 'Components/Button';
import {
  AddSerializedEquipmentModal,
  EditSerializedEquipmentModal,
  RetireAssetModal,
  CheckOutModal,
  SerializedEquipmentPool,
} from 'Components/SerializedEquipment';

import { SerializedEquipmentTimeline } from '../SerializedEquipmentTimeline/SerializedEquipmentTimeline';
import { SerializedEquipmentTypes } from '../SerializedEquipmentTypes/SerializedEquipmentTypes';
import { AssetHistoryModal } from '../AssetHistoryModal/AssetHistoryModal';
import { DeployAssetModal } from '../DeployAssetModal/DeployAssetModal';
import {
  listCompanyEquipmentCatalog,
  listCompanyEquipmentAssets,
  registerCompanyEquipmentAsset,
  checkOutAsset,
  updateEquipmentAsset,
  retireEquipmentAsset,
  getEquipmentAsset,
} from '../actions';
import { CatalogItem, EquipmentAsset, RegisterAssetPayload, UpdateAssetPayload } from '../types';

import classes from './serializedEquipmentInventory.module.css';

/**
 * Serialized (per-unit asset) Equipment Inventory page, shown when `serializedEquipment` is ON.
 * Lets the company register serialized units into its asset pool, lists the pool, and shows the
 * where-is-my-equipment Gantt. Calls only serialized `/equipment-assets` + catalog endpoints.
 */
function SerializedEquipmentInventoryContainer() {
  const dispatch = useDispatch();
  const companyId = useSelector(firstCompanyIdSelector, areEqual);

  const [catalog, setCatalog] = useState<CatalogItem[]>([]);
  const [assets, setAssets] = useState<EquipmentAsset[]>([]);
  const [fetchingAssets, setFetchingAssets] = useState(true);
  const [loadingMoreAssets, setLoadingMoreAssets] = useState(false);
  const [assetsFailed, setAssetsFailed] = useState(false);
  const [assetsPage, setAssetsPage] = useState(1);
  const [assetsLastPage, setAssetsLastPage] = useState(1);
  const [assetsTotal, setAssetsTotal] = useState(0);
  const assetsLoadingRef = useRef(false); // guards overlapping pool requests
  const assetsLoadedRef = useRef(false); // true once the pool has loaded at least once
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);
  // Bumped after deploy/move/check-out so the Gantt reloads (it's a history view of placements).
  const [timelineReload, setTimelineReload] = useState(0);
  // Check-out modal (optional back-dated date_out).
  const [checkoutTarget, setCheckoutTarget] = useState<EquipmentAsset | null>(null);
  const [checkoutSubmitting, setCheckoutSubmitting] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [historyAsset, setHistoryAsset] = useState<EquipmentAsset | null>(null);
  const [deployAsset, setDeployAsset] = useState<EquipmentAsset | null>(null);
  const [deployMode, setDeployMode] = useState<'deploy' | 'move'>('deploy');
  const [editAsset, setEditAsset] = useState<EquipmentAsset | null>(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [retireTarget, setRetireTarget] = useState<EquipmentAsset | null>(null);
  const [retireSubmitting, setRetireSubmitting] = useState(false);
  const [retireError, setRetireError] = useState<string | null>(null);

  // The pool endpoint is paginated (default 25/page): page 1 replaces, later pages append via Load
  // More. Mutations that change the pool reload from page 1.
  const loadAssets = useCallback(
    async (page = 1) => {
      if (!companyId) {
        setFetchingAssets(false);
        return;
      }
      if (assetsLoadingRef.current) return; // ignore overlapping requests
      assetsLoadingRef.current = true;
      if (page > 1) setLoadingMoreAssets(true);
      else setFetchingAssets(true);
      const response: any = await dispatch(listCompanyEquipmentAssets(companyId, { page }));
      if (!response) {
        // Read failed. On the first-ever load show a retryable error; on a later refresh keep the
        // data already on screen and just warn, rather than blanking the list.
        if (page === 1 && !assetsLoadedRef.current) setAssetsFailed(true);
        else dispatch(setToaster('Couldn’t refresh equipment. Please try again.', false));
        setFetchingAssets(false);
        setLoadingMoreAssets(false);
        assetsLoadingRef.current = false;
        return;
      }
      setAssetsFailed(false);
      assetsLoadedRef.current = true;
      const data: EquipmentAsset[] = response?.data ?? [];
      setAssets((prev) => (page > 1 ? [...prev, ...data] : data));
      setAssetsPage(response?.meta?.current_page ?? page);
      setAssetsLastPage(response?.meta?.last_page ?? 1);
      setAssetsTotal(response?.meta?.total ?? data.length);
      setFetchingAssets(false);
      setLoadingMoreAssets(false);
      assetsLoadingRef.current = false;
    },
    [companyId, dispatch]
  );

  const onLoadMoreAssets = useCallback(() => loadAssets(assetsPage + 1), [loadAssets, assetsPage]);

  const loadCatalog = useCallback(async () => {
    if (!companyId) return;
    const response: any = await dispatch(listCompanyEquipmentCatalog(companyId));
    if (!response) {
      // Catalog feeds the register dropdown; warn rather than silently offering no types.
      dispatch(setToaster('Couldn’t load equipment types for the register form. Please try again.', false));
      return;
    }
    setCatalog(response?.data ?? []);
  }, [companyId, dispatch]);

  useEffect(() => {
    loadAssets();
    loadCatalog();
  }, [loadAssets, loadCatalog]);

  const onAddClick = useCallback(() => {
    setRegisterError(null);
    setModalOpen(true);
  }, []);
  const onCloseModal = useCallback(() => setModalOpen(false), []);

  const onHistory = useCallback((asset: EquipmentAsset) => setHistoryAsset(asset), []);
  const onCloseHistory = useCallback(() => setHistoryAsset(null), []);

  // Check-out opens a modal (for the optional back-dated date_out). One idempotency key per asset,
  // reused across retries until success (backend dedupes replays); dropped on success.
  const checkoutKeys = useRef<Record<number, string>>({});

  const onCheckOut = useCallback((asset: EquipmentAsset) => {
    setCheckoutError(null);
    setCheckoutTarget(asset);
  }, []);
  const onCloseCheckout = useCallback(() => setCheckoutTarget(null), []);

  const onConfirmCheckout = useCallback(
    async (dateOut?: string) => {
      const asset = checkoutTarget;
      if (!asset) return;
      const idempotencyKey = checkoutKeys.current[asset.id] ?? uuidv4();
      checkoutKeys.current[asset.id] = idempotencyKey;
      setCheckoutSubmitting(true);
      setCheckoutError(null);
      const response: any = await dispatch(
        checkOutAsset(asset.id, { updated_at: asset.updated_at, date_out: dateOut, idempotency_key: idempotencyKey })
      );
      setCheckoutSubmitting(false);
      if (response?.data) {
        delete checkoutKeys.current[asset.id];
        setCheckoutTarget(null);
        loadAssets();
        setTimelineReload((n) => n + 1);
        return;
      }
      // On a 409 the asset's updated_at is stale — refresh that unit and re-point the modal at it so
      // the retry carries the current token. Surface the backend's 409/422 message either way.
      if (response?.error?.status === 409) {
        const fresh: any = await dispatch(getEquipmentAsset(asset.id));
        if (fresh?.data) {
          setAssets((prev) => prev.map((item) => (item.id === asset.id ? fresh.data : item)));
          setCheckoutTarget(fresh.data);
        }
      }
      setCheckoutError(response?.error?.message || 'Couldn’t check out this unit. Please try again.');
    },
    [checkoutTarget, dispatch, loadAssets]
  );

  const onDeploy = useCallback((asset: EquipmentAsset) => {
    setDeployAsset(asset);
    setDeployMode('deploy');
  }, []);

  const onMove = useCallback((asset: EquipmentAsset) => {
    setDeployAsset(asset);
    setDeployMode('move');
  }, []);

  const onCloseDeploy = useCallback(() => setDeployAsset(null), []);

  const onDeploySuccess = useCallback(() => {
    setDeployAsset(null);
    loadAssets();
    setTimelineReload((n) => n + 1);
  }, [loadAssets]);

  // Move hit a 409 (stale updated_at): refresh just that unit and re-point the open modal at it so
  // the user's retry sends the current optimistic-lock token.
  const onDeployConflict = useCallback(async () => {
    if (!deployAsset) return;
    const fresh: any = await dispatch(getEquipmentAsset(deployAsset.id));
    if (fresh?.data) {
      setAssets((prev) => prev.map((item) => (item.id === fresh.data.id ? fresh.data : item)));
      setDeployAsset(fresh.data);
    }
  }, [deployAsset, dispatch]);

  const onEdit = useCallback((asset: EquipmentAsset) => {
    setEditError(null);
    setEditAsset(asset);
  }, []);
  const onCloseEdit = useCallback(() => setEditAsset(null), []);
  const onSubmitEdit = useCallback(
    async (payload: UpdateAssetPayload) => {
      if (!editAsset) return;
      setEditSubmitting(true);
      setEditError(null);
      const response: any = await dispatch(updateEquipmentAsset(editAsset.id, payload));
      setEditSubmitting(false);
      if (response?.data) {
        setEditAsset(null);
        loadAssets();
      } else {
        setEditError(
          response?.error?.message
            || 'Couldn’t save changes — the unit may have changed since you opened it. Close and reopen to try again.'
        );
      }
    },
    [editAsset, dispatch, loadAssets]
  );

  const onRetire = useCallback((asset: EquipmentAsset) => {
    setRetireError(null);
    setRetireTarget(asset);
  }, []);
  const onCloseRetire = useCallback(() => setRetireTarget(null), []);
  const onConfirmRetire = useCallback(async () => {
    if (!retireTarget) return;
    setRetireSubmitting(true);
    setRetireError(null);
    // Retire is DELETE -> 204; runMutation yields a string ('') on success (delete quirk),
    // or { error } on failure.
    const response: any = await dispatch(retireEquipmentAsset(retireTarget.id));
    setRetireSubmitting(false);
    if (typeof response === 'string') {
      setRetireTarget(null);
      loadAssets();
    } else {
      setRetireError(
        response?.error?.message || 'Couldn’t retire this unit. Make sure it’s checked out of its room first.'
      );
    }
  }, [retireTarget, dispatch, loadAssets]);

  const onSubmit = useCallback(
    async (payload: RegisterAssetPayload) => {
      if (!companyId) return;
      setSubmitting(true);
      setRegisterError(null);
      const response: any = await dispatch(registerCompanyEquipmentAsset(companyId, payload));
      setSubmitting(false);
      if (response?.data) {
        setModalOpen(false);
        loadAssets();
      } else {
        setRegisterError(response?.error?.message || 'Couldn’t register this unit. Please try again.');
      }
    },
    [companyId, dispatch, loadAssets]
  );

  return (
    <div className={classes.content} data-testid="serialized-equipment-inventory">
      <section className={classes.section}>
        <h2 className="h4 mb-3">Equipment Timeline</h2>
        <SerializedEquipmentTimeline reloadToken={timelineReload} />
      </section>

      <hr className={classes.divider} />

      <section className={classes.section}>
        <div className={classes.header}>
          <h2 className="h4 mb-0">Serialized Equipment</h2>
          <PurpleButton type="button" className={classes.addButton} onClick={onAddClick}>
            Add +
          </PurpleButton>
        </div>

        {assetsFailed && (
          <div className="d-flex flex-column align-items-center p-4" role="alert" data-testid="pool-error">
            <p className="text-muted mb-3">Couldn’t load equipment.</p>
            <button type="button" className="btn btn-outline-primary" onClick={() => loadAssets(1)}>
              Retry
            </button>
          </div>
        )}
        {!assetsFailed && fetchingAssets && <SpinnerBlock fetching />}
        {!assetsFailed && !fetchingAssets && (
          <SerializedEquipmentPool
            assets={assets}
            busyAssetId={checkoutSubmitting ? checkoutTarget?.id ?? null : null}
            onCheckOut={onCheckOut}
            onHistory={onHistory}
            onDeploy={onDeploy}
            onMove={onMove}
            onEdit={onEdit}
            onRetire={onRetire}
          />
        )}
        {loadingMoreAssets && <SpinnerBlock fetching />}
        {!fetchingAssets && !loadingMoreAssets && !assetsFailed && assetsPage < assetsLastPage && (
          <LoadMoreButton
            type="Equipment"
            totalItems={assetsTotal - assets.length}
            loadMoreItemsClick={onLoadMoreAssets}
          />
        )}
      </section>

      <hr className={classes.divider} />

      <section className={classes.section}>
        <h2 className="h4 mb-3">Equipment Types</h2>
        <SerializedEquipmentTypes onChanged={loadCatalog} />
      </section>

      <AddSerializedEquipmentModal
        isOpen={modalOpen}
        catalog={catalog}
        submitting={submitting}
        submitError={registerError}
        onSubmit={onSubmit}
        onClose={onCloseModal}
      />

      <AssetHistoryModal isOpen={!!historyAsset} asset={historyAsset} onClose={onCloseHistory} />

      <CheckOutModal
        isOpen={!!checkoutTarget}
        asset={checkoutTarget}
        submitting={checkoutSubmitting}
        submitError={checkoutError}
        onConfirm={onConfirmCheckout}
        onClose={onCloseCheckout}
      />

      <DeployAssetModal
        isOpen={!!deployAsset}
        asset={deployAsset}
        mode={deployMode}
        onClose={onCloseDeploy}
        onSuccess={onDeploySuccess}
        onConflict={onDeployConflict}
      />

      <EditSerializedEquipmentModal
        isOpen={!!editAsset}
        asset={editAsset}
        submitting={editSubmitting}
        submitError={editError}
        onSubmit={onSubmitEdit}
        onClose={onCloseEdit}
      />

      <RetireAssetModal
        isOpen={!!retireTarget}
        asset={retireTarget}
        submitting={retireSubmitting}
        submitError={retireError}
        onConfirm={onConfirmRetire}
        onClose={onCloseRetire}
      />
    </div>
  );
}

const SerializedEquipmentInventoryMemo = memo(SerializedEquipmentInventoryContainer, areEqual);

export { SerializedEquipmentInventoryMemo as SerializedEquipmentInventory };
